import PropTypes from 'prop-types';
import { useEffect } from 'react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import InfoAlert from 'src/components/InfoAlert';
import WarningAlert from 'src/components/WarningAlert';
import BackButton from 'src/components/BackButton';
import SuccessButton from 'src/components/SuccessButton';
import ManagedBasePolicyField from './ManagedBasePolicyField';

const schema = Yup.object().shape({
  basePolicyIds: Yup.array().of(Yup.string()).min(1, 'Select at least one base policy'),
});

// Choosing which base policies a component is generated for is the same act whether the component
// is a catalog or a supplemental policy, so the picker is shared and only its copy differs.
const BasePolicySelectionForm = ({
  basePolicies,
  basePoliciesLoading,
  description,
  initialBasePolicyIds,
  loading,
  nextVersion,
  onCancel,
  onSubmit,
  onUseExistingPolicy,
  onCreateManagedPolicy,
  requirementNote,
  submitDisabled,
  submitLabel,
  summary,
  warningNote,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: { basePolicyIds: initialBasePolicyIds },
    resolver: yupResolver(schema),
  });
  const selectedBasePolicyIds = watch('basePolicyIds') || [];

  useEffect(() => {
    reset({ basePolicyIds: initialBasePolicyIds });
  }, [initialBasePolicyIds, reset]);

  return (
    <form onSubmit={handleSubmit(values => onSubmit(values.basePolicyIds))}>
      <DialogContent>
        <Stack gap={2}>
          {summary}

          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {description}
          </Typography>

          <InfoAlert sx={{ py: 0.25 }}>
            <Typography variant="caption">{requirementNote}</Typography>
          </InfoAlert>

          <Controller
            name="basePolicyIds"
            control={control}
            render={({ field }) => (
              <ManagedBasePolicyField
                basePolicies={basePolicies}
                loading={basePoliciesLoading}
                isSelected={policyId => field.value.includes(policyId)}
                onToggle={(policyId, checked) =>
                  field.onChange(
                    checked
                      ? field.value.filter(id => id !== policyId)
                      : [...field.value, policyId],
                  )
                }
                error={errors.basePolicyIds?.message}
                onUseExistingPolicy={() => onUseExistingPolicy(selectedBasePolicyIds)}
                onCreateManagedPolicy={() => onCreateManagedPolicy(selectedBasePolicyIds)}
              />
            )}
          />

          <Typography variant="body2" sx={{ color: '#9CA3AF', fontWeight: 600 }}>
            {`Next version: ${nextVersion}`}
          </Typography>

          {warningNote ? (
            <WarningAlert sx={{ py: 0.25 }}>
              <Typography variant="caption">{warningNote}</Typography>
            </WarningAlert>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <BackButton onClick={onCancel}>Cancel</BackButton>
        <SuccessButton type="submit" loading={loading} disabled={loading || submitDisabled}>
          {submitLabel}
        </SuccessButton>
      </DialogActions>
    </form>
  );
};

BasePolicySelectionForm.propTypes = {
  basePolicies: PropTypes.arrayOf(
    PropTypes.shape({
      environmentDefault: PropTypes.bool,
      policyId: PropTypes.string.isRequired,
      policyName: PropTypes.string.isRequired,
    }),
  ).isRequired,
  basePoliciesLoading: PropTypes.bool,
  description: PropTypes.string.isRequired,
  initialBasePolicyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  loading: PropTypes.bool,
  nextVersion: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onUseExistingPolicy: PropTypes.func.isRequired,
  onCreateManagedPolicy: PropTypes.func.isRequired,
  requirementNote: PropTypes.string.isRequired,
  submitDisabled: PropTypes.bool,
  submitLabel: PropTypes.string.isRequired,
  summary: PropTypes.node,
  warningNote: PropTypes.node,
};

export default BasePolicySelectionForm;
